/**
 * React host of the "Used by" building block outside the editor. The rows
 * and the unknown badge are the Lit parts of the database block, so a dialog
 * shows exactly what the database shows and adds no text of its own.
 */
import {
  type DatabaseAppUsage,
  databaseAppUsageEffects,
  type DatabaseAppUsagePort,
  type DatabaseAppUsageRead,
  renderDatabaseAppUsageRow,
} from '@blocksuite/affine/blocks/database';
import { html, render, type TemplateResult } from 'lit';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

function rowsTemplate(usages: readonly DatabaseAppUsage[]) {
  return html`${usages.map(usage => renderDatabaseAppUsageRow(usage))}`;
}

function usageTemplate(read: DatabaseAppUsageRead) {
  if (read.status === 'unknown') {
    return html`<affine-database-app-usage-badge
      .read=${read}
    ></affine-database-app-usage-badge>`;
  }
  if (read.usages.length === 0) return null;
  return rowsTemplate(read.usages);
}

/** Renders the Lit parts of the building block into a React host. */
function UsageHost({ template }: { template: TemplateResult }): ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // The dialog can open before any editor registered the badge.
    databaseAppUsageEffects();
    render(template, host);
    return () => {
      render(null, host);
    };
  }, [template]);

  return (
    <div
      ref={hostRef}
      data-testid="database-app-usage-list"
      style={{ display: 'grid', gap: 4 }}
    />
  );
}

/**
 * Which apps read a database. Without a port (local workspace) it renders
 * nothing; an unknown read shows the unknown badge, never "no apps".
 */
export function DatabaseAppUsageList({
  databaseId,
  port,
}: {
  databaseId: string;
  port: DatabaseAppUsagePort | undefined;
}): ReactElement | null {
  const [read, setRead] = useState<DatabaseAppUsageRead>({
    status: 'unknown',
  });
  const template = useMemo(() => usageTemplate(read), [read]);

  useEffect(() => {
    if (!port) return;
    return port.watch(databaseId, setRead);
  }, [databaseId, port]);

  if (!port || !template) return null;
  return <UsageHost template={template} />;
}

/** The rows of usages already read, as a dialog that read them once shows them. */
export function DatabaseAppUsageRows({
  usages,
}: {
  usages: readonly DatabaseAppUsage[];
}): ReactElement {
  const template = useMemo(() => rowsTemplate(usages), [usages]);
  return <UsageHost template={template} />;
}
